import { useEffect, useRef, useState } from "react";
import {
  BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate,
} from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Home from "@/pages/Home";
import Story from "@/pages/Story";
import Leadership from "@/pages/Leadership";
import Jewellery from "@/pages/Jewellery";
import Wholesale from "@/pages/Wholesale";
import Retail from "@/pages/Retail";
import CategoryPage from "@/pages/CategoryPage";
import RatesPage from "@/pages/RatesPage";
import Catalogue from "@/pages/Catalogue";
import Appointment from "@/pages/Appointment";
import RealEstate from "@/pages/RealEstate";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import SiteVisit from "@/pages/SiteVisit";
import Landowner from "@/pages/Landowner";
import Contact from "@/pages/Contact";
import Media from "@/pages/Media";
import Legal from "@/pages/Legal";
import SeoJewelleryMeerut from "@/pages/SeoJewelleryMeerut";
import WholesaleJewelleryMeerut from "@/pages/seo/WholesaleJewelleryMeerut";
import JewelleryManufacturerMeerut from "@/pages/seo/JewelleryManufacturerMeerut";
import GoldJewelleryMeerut from "@/pages/seo/GoldJewelleryMeerut";
import DiamondJewelleryMeerut from "@/pages/seo/DiamondJewelleryMeerut";
import BridalJewelleryMeerut from "@/pages/seo/BridalJewelleryMeerut";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AuthCallback() {
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    api.post("/auth/session", { session_id: sessionId })
      .then((r) => navigate("/admin", { replace: true, state: { user: r.data } }))
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);
  return (
    <div className="min-h-screen bg-[#060A12] flex items-center justify-center" data-testid="auth-callback">
      <p className="font-serif italic text-2xl text-[#C5A059]">Signing you in…</p>
    </div>
  );
}

function AdminGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) return;
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login", { replace: true }))
      .finally(() => setChecking(false));
  }, [location.state, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#060A12] flex items-center justify-center">
        <p className="font-serif italic text-2xl text-[#C5A059]">Loading…</p>
      </div>
    );
  }
  if (!user) return null;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  return <AdminDashboard user={user} onLogout={logout} />;
}

function SiteLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
      <Route path="/our-story" element={<SiteLayout><Story /></SiteLayout>} />
      <Route path="/leadership" element={<SiteLayout><Leadership /></SiteLayout>} />
      <Route path="/jewellery-meerut" element={<SiteLayout><SeoJewelleryMeerut /></SiteLayout>} />
      <Route path="/wholesale-jewellery-meerut" element={<SiteLayout><WholesaleJewelleryMeerut /></SiteLayout>} />
      <Route path="/jewellery-manufacturer-meerut" element={<SiteLayout><JewelleryManufacturerMeerut /></SiteLayout>} />
      <Route path="/gold-jewellery-meerut" element={<SiteLayout><GoldJewelleryMeerut /></SiteLayout>} />
      <Route path="/diamond-jewellery-meerut" element={<SiteLayout><DiamondJewelleryMeerut /></SiteLayout>} />
      <Route path="/bridal-jewellery-meerut" element={<SiteLayout><BridalJewelleryMeerut /></SiteLayout>} />
      <Route path="/jewellery" element={<SiteLayout><Jewellery /></SiteLayout>} />
      <Route path="/jewellery/wholesale" element={<SiteLayout><Wholesale /></SiteLayout>} />
      <Route path="/jewellery/retail" element={<SiteLayout><Retail /></SiteLayout>} />
      <Route path="/jewellery/rates" element={<SiteLayout><RatesPage /></SiteLayout>} />
      <Route path="/jewellery/catalogue" element={<SiteLayout><Catalogue /></SiteLayout>} />
      <Route path="/jewellery/appointment" element={<SiteLayout><Appointment /></SiteLayout>} />
      <Route path="/jewellery/category/:slug" element={<SiteLayout><CategoryPage /></SiteLayout>} />
      <Route path="/jewellery/contact" element={<Navigate to="/contact?type=wholesale_jewellery" replace />} />
      <Route path="/real-estate" element={<SiteLayout><RealEstate /></SiteLayout>} />
      <Route path="/projects" element={<SiteLayout><Projects /></SiteLayout>} />
      <Route path="/projects/:slug" element={<SiteLayout><ProjectDetail /></SiteLayout>} />
      <Route path="/site-visit" element={<SiteLayout><SiteVisit /></SiteLayout>} />
      <Route path="/landowner-partnerships" element={<SiteLayout><Landowner /></SiteLayout>} />
      <Route path="/real-estate/contact" element={<Navigate to="/contact?type=property" replace />} />
      <Route path="/media" element={<SiteLayout><Media /></SiteLayout>} />
      <Route path="/contact" element={<SiteLayout><Contact /></SiteLayout>} />
      <Route path="/legal/:page" element={<SiteLayout><Legal /></SiteLayout>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminGate />} />
      <Route path="*" element={<SiteLayout><NotFound /></SiteLayout>} />
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="pt-40 pb-32 text-center" data-testid="not-found-page">
      <p className="font-serif text-7xl text-[#C5A059]/30">404</p>
      <h1 className="font-serif text-4xl text-[#FBF9F5] mt-4">This page does not exist.</h1>
      <a href="/" className="text-[#C5A059] text-sm mt-6 inline-block">Return home</a>
    </div>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppRouter />
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
